import axios from 'axios';
import { UNAUTHORIZED, FORBIDDEN, BAD_GATEWAY, GATEWAY_TIMEOUT } from 'http-status';
import omit from 'lodash/omit';
import store from './store'; // eslint-disable-line import/no-cycle
import buildinfo from './buildnum.json';

const {
  VUE_APP_API_PREFIX: apiPrefix,
  VUE_APP_HANDLE: appHandle,
  VUE_APP_AUTH_TOKEN: appAuthToken,
  VUE_APP_APP_NAME: appName,
  VUE_APP_APP_VERSION: appVersion
} = process.env;

const appBuild = buildinfo.buildnum || 'dev';

const KeyboardingAPI = axios.create();

const POST = 'post';
const GET = 'get';

KeyboardingAPI.interceptors.request.use(
  // eslint-disable-next-line arrow-parens
  config => {
    const {
      student: { sessionId }
    } = store.get();

    if (sessionId) {
      // eslint-disable-next-line no-param-reassign
      config.headers['x-lwtears-session'] = sessionId;
    } else {
      // eslint-disable-next-line no-param-reassign
      config.headers['x-ssousername'] = appHandle;
      // eslint-disable-next-line no-param-reassign
      config.headers['x-authtoken'] = decodeURIComponent(appAuthToken);
    }

    return config;
  },
  error => Promise.reject(error)
);

KeyboardingAPI.defaults.baseURL = apiPrefix;
KeyboardingAPI.defaults.headers.common['x-lwtears-app'] = appName;
KeyboardingAPI.defaults.headers.common['x-lwtears-app-version'] = appVersion;
KeyboardingAPI.defaults.headers.common['x-lwtears-app-build'] = appBuild;

const createLogEvent = (type, data, options) => {
  const timestamp = new Date().toISOString();
  try {
    KeyboardingAPI.post('/logs', { type, timestamp, data }, options);
  } catch (err) {}
};

const createEvent = (type, data) => {
  const timestamp = new Date().toISOString();
  try {
    KeyboardingAPI.post('/events', { type, timestamp, data });
  } catch (err) {}
};

const execute = async (method, url, { headers = {}, ...options } = {}, retry = false) => {
  try {
    const { status, data } = await KeyboardingAPI({
      method,
      url,
      headers,
      ...options
    });
    return {
      status,
      data
    };
  } catch (error) {
    const { response: { status, data } = {} } = error || {};
    if (status === UNAUTHORIZED || status === FORBIDDEN) return { status };

    const { data: body, ...opts } = options;
    createLogEvent('api-error', { method, url, body, ...opts, status, retry, data }, { headers });
    if (retry || (status !== BAD_GATEWAY && status !== GATEWAY_TIMEOUT)) return { status };

    return execute(method, url, { headers, ...options }, true);
  }
};

export default {
  async getStudent({ homeUseToken }) {
    const params = { homeUseToken };
    return execute(GET, '/students', { params });
  },

  async studentLogin(body) {
    return execute(POST, '/students/login', { data: body });
  },

  async addProgressData(activityType, sessionId, body) {
    const data = {
      ...omit(body, ['grade', 'productId', 'asn', 'week', 'speedyKid'])
    };

    const result = await execute(POST, '/students/me/progress', { data });

    const { status } = result;

    if (status < 400) {
      const { errorData } = body;
      const errorCount = errorData ? errorData.length : 0;
      createEvent('activityCompleted', {
        ...omit(body, ['errorData', 'responseData']),
        errorCount,
        activityType
      });
    }

    return result;
  },

  async addScheduledActivityData(activityType, sessionId, scheduledActivityId, body) {
    const data = {
      ...omit(body, ['grade', 'productId', 'asn', 'week', 'speedyKid', 'activityId'])
    };
    const result = await execute(POST, `/students/me/scheduled-activities/${scheduledActivityId}`, {
      data
    });
    const { status } = result;
    if (status < 400) {
      const { errorData } = body;
      const errorCount = errorData ? errorData.length : 0;
      createEvent('assessmentCompleted', {
        activityId: scheduledActivityId,
        ...omit(body, ['errorData', 'responseData']),
        errorCount,
        activityType
      });
    }
    return result;
  },

  async getStudentsByClassroom(classroomId) {
    return execute(GET, `/classrooms/${classroomId}/students`);
  },

  async getClassroomsByEducatorPin(pin) {
    const params = { pin };
    return execute(GET, '/classrooms', { params });
  },

  async getClassroomsByUserIds(userIds) {
    const params = { userId: userIds };
    return execute(GET, '/classrooms', { params });
  },

  async getCurriculum() {
    return execute(GET, '/students/me/curriculum');
  },

  async getNextActivity() {
    return execute(GET, '/students/me/next-item');
  },

  createEvent,
  createLogEvent
};
